import Link from "next/link";
import { Trans } from "@lingui/macro";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide.min.css";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { WhyUseLumiPageQuery } from "@graphql/generated/graphql";
import { generateDomainPath } from "@components/layout/shared/link-props";

interface IPublisherCarouselProps {
  data: WhyUseLumiPageQuery;
}

interface IPublisherList {
  publisher: WhyUseLumiPageQuery["languages"][0]["newsPublishers"][0];
}

const PublishersCarousel: React.FunctionComponent<IPublisherCarouselProps> = ({
  data,
}) => {
  const splideOptions: Object = {
    type: "loop",
    drag: "free",
    gap: "32px",
    arrows: false,
    perPage: 7,
    pagination: false,
    direction: "rtl",
    autoWidth: true,
    autoScroll: {
      pauseOnHover: true,
      pauseOnFocus: false,
      rewind: false,
      speed: 1,
    },
    breakpoints: {
      900: {
        perPage: 4,
        gap: "32px",
        autoScroll: {
          speed: 1,
        },
      },
      640: {
        perPage: 3,
        gap: "32px",
        autoScroll: {
          speed: 1,
        },
      },
      400: {
        perPage: 2,
        gap: "32px",
        autoScroll: {
          speed: 1,
        },
      },
    },
  };

  return (
    <div className="relative mt-16 mb-20 md:mt-40 md:mb-40">
      <Trans>
        <p className="mx-auto mb-16 max-w-sm text-center font-poppins text-[31px] font-bold leading-[1.3] md:mb-20 md:text-[39px]">
          Get real-time news from sources you{" "}
          <span className="text-primary">trust</span>
          ⚡️
        </p>
      </Trans>
      <Splide options={splideOptions} extensions={{ AutoScroll }}>
        {data.languages
          .find((language) => language.code === "en")
          .newsPublishers.filter(
            (publisher) =>
              !(
                publisher.name == "South China Morning Post" ||
                publisher.name == "CNA"
              )
          )
          .map((publisher) => (
            <SplideSection publisher={publisher} key={publisher.id} />
          ))}
      </Splide>
      <Splide
        options={{ ...splideOptions, padding: "5rem" }}
        extensions={{ AutoScroll }}
      >
        {data.languages
          .find((language) => language.code === "zh")
          .newsPublishers.map((publisher) => (
            <SplideSection publisher={publisher} key={publisher.id} />
          ))}
      </Splide>
      <Splide options={splideOptions} extensions={{ AutoScroll }}>
        {data.languages
          .find((language) => language.code === "ms")
          .newsPublishers.map((publisher) => (
            <SplideSection publisher={publisher} key={publisher.id} />
          ))}
      </Splide>
    </div>
  );
};

const SplideSection: React.FunctionComponent<IPublisherList> = ({
  publisher,
}) => {
  return (
    <SplideSlide key={publisher.id}>
      <Link href={generateDomainPath(`/publishers/${publisher.slug}`)} passHref>
        <a target="_blank">
          <div className="mb-10 mt-4 flex flex-col items-center">
            <div
              className="relative h-14 w-24"
              style={{
                filter: "drop-shadow(1px 1px 8px rgba(147, 154, 188, 0.4)",
              }}
            >
              <img
                className="publisher-carousel-img rounded-lg"
                src={publisher.imageUrl}
                alt={publisher.name}
              />
            </div>
          </div>
        </a>
      </Link>
    </SplideSlide>
  );
};

export default PublishersCarousel;
